import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TreatmentOutcomesTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return data;
    
    return [...data]?.sort((a, b) => {
      if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const getOutcomeColor = (rate, benchmark) => {
    const diff = rate - benchmark;
    if (diff >= 5) return 'text-success';
    if (diff <= -5) return 'text-error';
    return 'text-warning';
  };

  const getOutcomeIcon = (rate, benchmark) => {
    const diff = rate - benchmark;
    if (diff >= 5) return 'TrendingUp';
    if (diff <= -5) return 'TrendingDown';
    return 'Minus';
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Treatment Outcomes</h2>
          <p className="text-sm text-muted-foreground mt-1">Clinical quality indicators vs national benchmarks</p>
        </div>
        <Icon name="Activity" size={24} color="var(--color-primary)" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b-2 border-border">
              <th 
                className="text-left py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('condition')}
              >
                <div className="flex items-center gap-2">
                  Condition/Procedure
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-center py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('successRate')}
              >
                <div className="flex items-center justify-center gap-2">
                  Success Rate
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">
                National Benchmark
              </th>
              <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">
                Variance
              </th>
              <th 
                className="text-center py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('cases')}
              >
                <div className="flex items-center justify-center gap-2">
                  Cases
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">
                Trend
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((item) => {
              const variance = item?.successRate - item?.benchmark;
              return (
                <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="py-4 px-4 text-sm font-medium text-foreground">{item?.condition}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`text-sm font-semibold ${getOutcomeColor(item?.successRate, item?.benchmark)}`}>
                      {item?.successRate}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center text-sm text-muted-foreground">
                    {item?.benchmark}%
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Icon 
                        name={getOutcomeIcon(item?.successRate, item?.benchmark)} 
                        size={14} 
                        className={getOutcomeColor(item?.successRate, item?.benchmark)}
                      />
                      <span className={`text-sm font-medium ${getOutcomeColor(item?.successRate, item?.benchmark)}`}>
                        {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center text-sm text-foreground font-medium whitespace-nowrap">
                    {item?.cases?.toLocaleString()}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Icon 
                        name={item?.trend === 'up' ? 'TrendingUp' : item?.trend === 'down' ? 'TrendingDown' : 'Minus'} 
                        size={16} 
                        color={item?.trend === 'up' ? 'var(--color-success)' : item?.trend === 'down' ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TreatmentOutcomesTable;